import React from 'react';
import { View, Text, Image, StyleSheet, TouchableOpacity } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useTheme } from '../../context/ThemeContext';
import { resolveEventCover } from '../../utils/coverResolver';
import type { FeedItem } from '../../types/auth';
import { LIVE_SESSION_TERM } from '../../utils/liveSessionDisplay';
import { eventCardStatus, isPastEventKind } from '../../utils/eventCardStatus';
import { readEventDirections } from '../../constants/directions';
import { useUnifiedLiveEnabled } from '../../hooks/useFeatureFlags';
import { pluralRu } from '../master-profile/plural';
import { fontWeights } from '../../styles/typographyTokens';

/**
 * Compact «событие»-card used by the master-profile «События» tab and the main
 * «Лента» — one shared card so both surfaces look identical. Cover (16:9) with a
 * kind badge and a status pill («идёт сейчас» / «завершена»), then title,
 * direction chips and date·time·duration·participants meta. Driven by a
 * `FeedItem`; the caller owns the tap target via `onPress` and, for owner
 * surfaces, the ⋮ menu via `onOpenMenu`.
 */

const ACCENT = '#E98245';
const LIVE_NOW = '#22C55E';
const PAST_PILL = 'rgba(15,20,25,0.62)';
const RU_MON = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];
const pad = (n: number) => String(n).padStart(2, '0');

/** Type badge (colour + label) per event kind. */
export function kindMeta(kind: FeedItem['kind']): { label: string; color: string } {
  if (kind.startsWith('live_')) return { label: 'Live-сессия', color: '#E11D48' };
  if (kind.startsWith('offline_')) return { label: 'Офлайн', color: '#2563EB' };
  if (kind.startsWith('room_')) return { label: 'Live-практика', color: '#10B981' };
  return { label: '', color: ACCENT };
}

function formatWhen(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getDate()} ${RU_MON[d.getMonth()]} · ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Start ≤ now < start + duration — the session is running right now. */
function isLiveNow(item: FeedItem, now: number = Date.now()): boolean {
  if (!item.startDateTimeUtc) return false;
  const start = new Date(item.startDateTimeUtc).getTime();
  if (!Number.isFinite(start)) return false;
  return start <= now && start + (item.durationMinutes ?? 60) * 60_000 > now;
}

/**
 * Status pill on the cover, mirroring the web card (CORE-740): a running
 * session says «ИДЁТ СЕЙЧАС», a finished one «ЗАВЕРШЕНА» / «ЗАВЕРШЕНО».
 * Upcoming events carry no pill — the kind badge already names the format.
 */
function statusPill(item: FeedItem): { label: string; color: string } | null {
  if (isPastEventKind(item.kind)) return { label: eventCardStatus(item.kind).label, color: PAST_PILL };
  if (item.kind !== 'practice_uploaded' && isLiveNow(item)) return { label: 'ИДЁТ СЕЙЧАС', color: LIVE_NOW };
  return null;
}

function participantsLabel(count: number): string {
  return `${count} ${pluralRu(count, ['участник', 'участника', 'участников'])}`;
}

interface Props {
  item: FeedItem;
  onPress: () => void;
  /** Owner-only ⋮ in the cover's top-right corner. Absent ⇒ no button. */
  onOpenMenu?: () => void;
  /**
   * CORE-1731 — cover on the left, text on the right, as the web event card.
   * For windows wider than a phone, where a full-width 16:9 cover is taller
   * than the screen. Absent ⇒ the vertical card, unchanged.
   */
  row?: boolean;
}

export const EventListCard: React.FC<Props> = ({ item, onPress, onOpenMenu, row = false }) => {
  const { colors, isDark } = useTheme();
  const unifiedLive = useUnifiedLiveEnabled();
  const meta = kindMeta(item.kind);
  // CORE-589 — unify the room/practice badge into the single «Live-сессия» term
  // when the unified flow is on (flag off keeps the legacy «Live-практика»).
  const badgeLabel =
    unifiedLive && item.kind.startsWith('room_') ? LIVE_SESSION_TERM : meta.label;
  // CORE-522 — no uploaded cover → branded default image (resolveEventCover),
  // not a blank «image-outline» placeholder. A cover-less live session created
  // in the wizard now shows the default cover in «События», like it used to.
  const cover = resolveEventCover([item.coverImageUrl]);
  const status = statusPill(item);
  const directions = readEventDirections(item);
  const chipBg = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,20,25,0.06)';
  const isOffline = item.kind.startsWith('offline_');
  // Offline events carry no live participant count — same rule as the web card.
  const participants = !isOffline && typeof item.participantsCount === 'number' && item.participantsCount > 0
    ? item.participantsCount
    : null;
  // CORE-1629 — parity with web PublicMasterEventCard testIDs for native e2e
  // (Profile → «События»). Non-offline cards keep event-card-menu.
  const menuTestId = isOffline ? 'offline-event-card-menu' : 'event-card-menu';

  return (
    <TouchableOpacity
      activeOpacity={0.85}
      onPress={onPress}
      style={[styles.card, row && styles.cardRow, { backgroundColor: colors.surfacePrimary, borderColor: colors.border }]}
    >
      <View style={[row ? styles.coverRow : styles.cover, { backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' }]}>
        {cover ? (
          <Image source={{ uri: cover }} style={row ? styles.coverImgRow : styles.coverImg} />
        ) : (
          <Ionicons name="image-outline" size={26} color={colors.textMuted} />
        )}
        {!!badgeLabel && (
          <View style={[styles.badge, { backgroundColor: meta.color }]}>
            <Text style={styles.badgeText}>{badgeLabel}</Text>
          </View>
        )}
        {status ? (
          <View style={[styles.status, { backgroundColor: status.color }, onOpenMenu && styles.statusBesideMenu]}>
            {status.color === LIVE_NOW ? <View style={styles.statusDot} /> : null}
            <Text style={styles.badgeText}>{status.label}</Text>
          </View>
        ) : null}
        {onOpenMenu ? (
          <TouchableOpacity
            testID={menuTestId}
            onPress={onOpenMenu}
            activeOpacity={0.8}
            accessibilityRole="button"
            accessibilityLabel="Управление событием"
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            style={styles.menuButton}
          >
            <Ionicons name="ellipsis-vertical" size={18} color="#FFF" accessible={false} />
          </TouchableOpacity>
        ) : null}
      </View>
      <View style={[styles.cardBody, row && styles.cardBodyRow]}>
        <Text
          testID={isOffline ? 'offline-event-card-title' : undefined}
          style={[styles.cardTitle, { color: colors.textPrimary }]}
          numberOfLines={2}
        >
          {item.title || 'Без названия'}
        </Text>
        {directions.length > 0 ? (
          <View style={styles.dirRow}>
            {directions.map((dir) => (
              <View key={dir} style={[styles.dirChip, { backgroundColor: chipBg }]}>
                <Text style={[styles.dirText, { color: colors.textSecondary }]}>{dir}</Text>
              </View>
            ))}
          </View>
        ) : null}
        <View style={styles.metaRow}>
          <Ionicons name="calendar-outline" size={13} color={colors.textMuted} />
          <Text style={[styles.metaText, { color: colors.textSecondary }]}>{formatWhen(item.startDateTimeUtc)}</Text>
          {item.durationMinutes ? (
            <>
              <Text style={[styles.metaDot, { color: colors.textMuted }]}>·</Text>
              <Text style={[styles.metaText, { color: colors.textSecondary }]}>{item.durationMinutes} мин</Text>
            </>
          ) : null}
          {participants != null ? (
            <>
              <Text style={[styles.metaDot, { color: colors.textMuted }]}>·</Text>
              <Ionicons name="people-outline" size={13} color={colors.textMuted} />
              <Text style={[styles.metaText, { color: colors.textSecondary }]}>{participantsLabel(participants)}</Text>
            </>
          ) : null}
        </View>
      </View>
    </TouchableOpacity>
  );
};

export default EventListCard;

const styles = StyleSheet.create({
  card: { borderRadius: 16, borderWidth: 1, overflow: 'hidden' },
  cover: { width: '100%', aspectRatio: 16 / 9, alignItems: 'center', justifyContent: 'center' },
  // Row card: web PublicMasterEventCard proportions; height follows the text.
  cardRow: { flexDirection: 'row' },
  coverRow: { width: '34%', minWidth: 220, maxWidth: 310, minHeight: 150, alignItems: 'center', justifyContent: 'center' },
  cardBodyRow: { flex: 1, justifyContent: 'center' },
  coverImg: { width: '100%', height: '100%' },
  // In flow, `height: '100%'` of the auto-height row cover resolves against the
  // ScrollView's free height (Yoga), so the card grew to a screen. Out of flow
  // the cover's height is the body's or minHeight.
  coverImgRow: StyleSheet.absoluteFillObject,
  badge: { position: 'absolute', top: 10, left: 10, paddingHorizontal: 10, paddingVertical: 4, borderRadius: 999 },
  badgeText: { color: '#FFF', fontSize: 11, fontWeight: fontWeights.bold, letterSpacing: 0.3 },
  status: {
    position: 'absolute', top: 10, right: 10,
    flexDirection: 'row', alignItems: 'center', gap: 6,
    paddingHorizontal: 10, paddingVertical: 4, borderRadius: 999,
  },
  statusBesideMenu: { right: 46 },
  statusDot: { width: 6, height: 6, borderRadius: 3, backgroundColor: '#FFF' },
  menuButton: {
    position: 'absolute', top: 6, right: 6,
    width: 32, height: 32, borderRadius: 16,
    alignItems: 'center', justifyContent: 'center',
    backgroundColor: 'rgba(15,20,25,0.55)',
  },
  cardBody: { padding: 14, gap: 8 },
  cardTitle: { fontSize: 16, fontWeight: fontWeights.bold, lineHeight: 21 },
  dirRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  dirChip: { paddingHorizontal: 9, paddingVertical: 3, borderRadius: 999 },
  dirText: { fontSize: 11, fontWeight: fontWeights.bold },
  metaRow: { flexDirection: 'row', alignItems: 'center', gap: 5, flexWrap: 'wrap' },
  metaText: { fontSize: 13, fontWeight: '600' },
  metaDot: { fontSize: 13 },
});
